import React from "react";
import {
  Box,
  Typography,
  IconButton,
  Divider,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import themestyle from "../theme";

export interface SchedulePreviewProps {
  selectedDates: string[];
  onRemoveDate: (date: string) => void;
  onAddDate: () => void;
  isViewMode?: boolean;
}

const getWeekday = (dateString: string) => {
  const date = new Date(dateString + "T00:00:00");
  return new Intl.DateTimeFormat("en-US", { weekday: "short" }).format(date).toUpperCase();
};


const SchedulePreview: React.FC<SchedulePreviewProps> = ({
  selectedDates,
  onRemoveDate,
  onAddDate,
  isViewMode = false,
}) => {
  if (selectedDates.length === 0) return null;

  const sortedDates = [...selectedDates].sort();
  const startDate = sortedDates[0];
  const endDate = sortedDates[sortedDates.length - 1];

  const startFormatted = new Date(startDate + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" });
  const endFormatted = new Date(endDate + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" });

  return (
    <Box
      sx={{
        border: "1px solid #E5E7EB",
        borderRadius: "8px",
        overflow: "hidden",
        mb: 3,
      }}
    >
      <Box
        sx={{
          p: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          bgcolor: "#fff",
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: "12px",
              fontWeight: 700,
              color: "#9CA3AF",
              letterSpacing: "1px",
              mb: 0.5,
            }}
          >
            SCHEDULE PREVIEW
          </Typography>
          <Typography sx={{ fontSize: "16px", fontWeight: 700, color: "#111827" }}>
            {startFormatted} - {endFormatted} |{" "}
            <span style={{ color: themestyle.colors.activeGreen }}>
              {selectedDates.length} Classes
            </span>
          </Typography>
        </Box>
        {!isViewMode && (
          <Box
            onClick={onAddDate}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              cursor: "pointer",
              color: themestyle.colors.activeGreen,
              fontWeight: 600,
              fontSize: "14px",
              "&:hover": { opacity: 0.8 },
            }}
          >
            Add Date
          </Box>
        )}
      </Box>

      <Divider />

      <Box
        sx={{
          maxHeight: "300px",
          overflowY: "auto",
          "&::-webkit-scrollbar": { width: "6px" },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#E5E7EB",
            borderRadius: "10px",
          },
        }}
      >
        {sortedDates.map((date, index) => (
          <Box
            key={date}
            sx={{
              p: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              borderBottom: index === sortedDates.length - 1 ? "none" : "1px solid #F3F4F6",
              "&:hover": { bgcolor: "#FAFAFA" },
            }}
          >
            <Box display="flex" alignItems="center" gap={4}>
              <Typography
                sx={{
                  fontSize: "12px",
                  fontWeight: 700,
                  color: "#9CA3AF",
                  width: "40px",
                }}
              >
                {getWeekday(date)}
              </Typography>
              <Typography sx={{ fontSize: "14px", fontWeight: 500, color: "#374151" }}>
                {new Date(date + "T00:00:00").toLocaleDateString("en-US", {
                   month: "short",
                   day: "numeric",
                   year: "numeric"
                })}
              </Typography>
            </Box>
            {!isViewMode && (
              <IconButton size="small" onClick={() => onRemoveDate(date)}>
                <DeleteIcon sx={{ fontSize: 18, color: themestyle.colors.error }} />
              </IconButton>
            )}
          </Box>
        ))}
      </Box>

      <Box
        sx={{
          bgcolor: "#F9FAFB",
          p: 1.5,
          textAlign: "center",
        }}
      >
        <Typography sx={{ fontSize: "12px", color: "#9CA3AF" }}>
          Scroll to view all generated dates
        </Typography>
      </Box>
    </Box>
  );
};

export default SchedulePreview;
